import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { useI18n } from "@/i18n";
import { AppLayout } from "@/components/AppLayout";
import { Button } from "@/components/ui/button";
import { Newspaper, RefreshCw, ExternalLink, Loader2, Landmark, MapPin } from "lucide-react";

const catColor = (c) => ({
  Auto: "bg-blue-50 text-blue-600", Home: "bg-amber-50 text-amber-600", Health: "bg-rose-50 text-rose-600",
  Life: "bg-violet-50 text-violet-600", Flood: "bg-cyan-50 text-cyan-600",
}[c] || "bg-slate-100 text-slate-500");

const UpdateCard = ({ it }) => (
  <a href={it.source_url} target="_blank" rel="noopener noreferrer" data-testid="update-card"
    className="group block rounded-2xl bg-white border border-slate-100 card-shadow p-5 transition-all hover:border-brandblue/40 hover:-translate-y-0.5">
    <div className="flex items-center justify-between gap-3 mb-2">
      <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${catColor(it.category)}`}>{it.category || "General"}</span>
      {it.date && <span className="text-xs text-slate-400">{it.date}</span>}
    </div>
    <p className="font-semibold text-navy leading-snug">{it.title}</p>
    <p className="mt-1.5 text-sm text-slate-500 leading-relaxed">{it.summary}</p>
    <div className="mt-3 flex items-center gap-1.5 text-xs font-medium text-brandblue">
      <ExternalLink className="h-3.5 w-3.5" />
      <span className="truncate">{it.source_name || it.source_url}</span>
    </div>
  </a>
);

export default function Updates() {
  const { t } = useI18n();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const pollRef = useRef(null);
  const tries = useRef(0);

  const load = async () => {
    try {
      const r = await api.get("/updates");
      setData(r.data);
      return r.data;
    } catch { return null; }
  };

  const startPoll = () => {
    clearInterval(pollRef.current);
    tries.current = 0;
    pollRef.current = setInterval(async () => {
      tries.current += 1;
      const d = await load();
      const has = d && (d.federal?.length || d.state?.items?.length);
      if ((d && !d.refreshing && has) || tries.current > 20) {
        clearInterval(pollRef.current);
        setRefreshing(false);
      }
    }, 6000);
  };

  useEffect(() => {
    (async () => {
      setLoading(true);
      const d = await load();
      setLoading(false);
      if (d?.refreshing) { setRefreshing(true); startPoll(); }
    })();
    return () => clearInterval(pollRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const refresh = async () => {
    setRefreshing(true);
    try { await api.post("/updates/refresh"); } catch {}
    startPoll();
  };

  const federal = data?.federal || [];
  const state = data?.state;

  return (
    <AppLayout>
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-semibold text-navy flex items-center gap-2" data-testid="updates-title">
            <Newspaper className="h-7 w-7 text-brandblue" /> {t("updates_title")}
          </h1>
          <p className="mt-1 text-slate-500 max-w-2xl">{t("updates_sub")}</p>
        </div>
        <Button data-testid="updates-refresh" onClick={refresh} disabled={refreshing}
          variant="outline" className="rounded-full shrink-0">
          <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? "animate-spin" : ""}`} /> {t("updates_refresh")}
        </Button>
      </div>

      {loading ? (
        <div className="mt-16 flex flex-col items-center justify-center gap-3 text-slate-400">
          <Loader2 className="h-8 w-8 animate-spin text-brandblue" />
          <p>{t("updates_loading")}</p>
        </div>
      ) : (
        <div className="mt-8 space-y-10">
          <section>
            <div className="flex items-center gap-2 mb-4">
              <Landmark className="h-5 w-5 text-brandteal" />
              <h2 className="text-lg font-semibold text-navy">{t("updates_federal")}</h2>
            </div>
            {federal.length ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="updates-federal">
                {federal.map((it, i) => <UpdateCard key={i} it={it} />)}
              </div>
            ) : (
              <p className="text-sm text-slate-400">{refreshing ? t("updates_loading") : t("updates_empty")}</p>
            )}
          </section>

          {state && (
            <section>
              <div className="flex items-center gap-2 mb-4">
                <MapPin className="h-5 w-5 text-brandteal" />
                <h2 className="text-lg font-semibold text-navy">{t("updates_your_state")} · {state.name} ({state.code})</h2>
              </div>
              {state.items?.length ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="updates-state">
                  {state.items.map((it, i) => <UpdateCard key={i} it={it} />)}
                </div>
              ) : (
                <p className="text-sm text-slate-400">{refreshing ? t("updates_loading") : t("updates_empty")}</p>
              )}
            </section>
          )}

          <p className="text-xs text-slate-400 border-t border-slate-100 pt-4">{t("updates_disclaimer")}</p>
        </div>
      )}
    </AppLayout>
  );
}
